import "react";
import { useQuery } from "@tanstack/react-query";
import {
  Mail,
  FileText,
  Users,
  Link as LinkIcon,
  Server,
  MessageSquare,
  Loader2,
  AlertCircle,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { billingApi } from "@/services/api";
import { formatDate } from "@/utils/dateFormat";

export const Usage = () => {
  const { t } = useTranslation();

  // ============================================
  // TanStack Query - Fetch Usage Data
  // ============================================
  const { data: usageData, isLoading: loading } = useQuery({
    queryKey: ["billing-usage"],
    queryFn: async () => {
      // /billing/subscription already bundles usage stats, per-limit usage,
      // and credit balances for the organisation.
      const subRes = await billingApi.getSubscription();
      const subscription = subRes.data.data;
      return {
        usage: subscription.usage,
        limits: subscription.limitsAndUsage,
        subscription,
      };
    },
    meta: {
      onError: () => {
        toast.error("Failed to load usage data");
      },
    },
  });

  const usage = usageData?.usage;
  const limits = usageData?.limits;
  const subscription = usageData?.subscription;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const periodStart = usage?.periodStart
    ? new Date(usage.periodStart)
    : new Date();
  const periodEnd = usage?.periodEnd ? new Date(usage.periodEnd) : new Date();
  const daysInPeriod = Math.ceil(
    (periodEnd - periodStart) / (1000 * 60 * 60 * 24),
  );
  const daysPassed = Math.ceil(
    (new Date() - periodStart) / (1000 * 60 * 60 * 24),
  );
  const periodProgress = Math.min(
    100,
    Math.round((daysPassed / daysInPeriod) * 100),
  );

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("common.usage")}
          </h2>
          <p className="text-muted-foreground">
            {t("billing.trackConsumption")}
          </p>
        </div>
      </div>

      {/* Billing Period */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-medium">
                {t("billing.currentBillingPeriod")}
              </CardTitle>
              <CardDescription>
                {formatDate(periodStart)} -{" "}
                {formatDate(periodEnd)}
              </CardDescription>
            </div>
            <Calendar className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                Day {daysPassed} of {daysInPeriod}
              </span>
              <span>{periodProgress}% complete</span>
            </div>
            <Progress value={periodProgress} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <UsageStatCard
          title={t("usage.envelopesSent")}
          value={usage?.envelopesSent || 0}
          limit={limits?.envelopes?.limit}
          icon={Mail}
          color="text-blue-500"
        />
        <UsageStatCard
          title={t("usage.templatesCreated")}
          value={usage?.templatesCreated || 0}
          limit={limits?.templates?.limit}
          icon={FileText}
          color="text-green-500"
        />
        <UsageStatCard
          title={t("usage.apiCalls")}
          value={usage?.apiCalls || 0}
          limit={limits?.apiCalls?.limit}
          icon={Server}
          color="text-purple-500"
        />
        <UsageStatCard
          title={t("usage.smsSent")}
          value={usage?.smsSent || 0}
          limit={null}
          icon={MessageSquare}
          color="text-orange-500"
          showCredits
        />
      </div>

      {/* Detailed Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Limits Usage */}
        <Card>
          <CardHeader>
            <CardTitle>{t("usage.resourceLimits")}</CardTitle>
            <CardDescription>{t("usage.resourceLimitsDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {limits &&
              Object.entries(limits).map(([key, data]) => (
                <UsageBar
                  key={key}
                  name={formatLimitName(key)}
                  used={data?.used ?? 0}
                  limit={data?.limit ?? 0}
                  unlimited={data?.unlimited ?? false}
                />
              ))}
          </CardContent>
        </Card>

        {/* Activity Summary */}
        <Card>
          <CardHeader>
            <CardTitle>{t("usage.activitySummary")}</CardTitle>
            <CardDescription>{t("usage.activitySummaryDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <ActivityItem
                label={t("usage.envelopesSent")}
                value={usage?.envelopesSent || 0}
                icon={Mail}
              />
              <ActivityItem
                label={t("usage.envelopesCompleted")}
                value={usage?.envelopesCompleted || 0}
                icon={FileText}
              />
              <ActivityItem
                label={t("usage.templatesCreated")}
                value={usage?.templatesCreated || 0}
                icon={FileText}
              />
              <ActivityItem
                label={t("usage.bulkSends")}
                value={usage?.bulkSends || 0}
                icon={Users}
              />
              <ActivityItem
                label={t("usage.signingLinksCreated")}
                value={usage?.signingLinksCreated || 0}
                icon={LinkIcon}
              />
              <ActivityItem
                label={t("usage.inPersonSessions")}
                value={usage?.inPersonSessions || 0}
                icon={Users}
              />
              <ActivityItem
                label={t("usage.apiCalls")}
                value={usage?.apiCalls || 0}
                icon={Server}
              />
              <ActivityItem
                label={t("usage.smsSent")}
                value={usage?.smsSent || 0}
                icon={MessageSquare}
              />
              <ActivityItem
                label={t("usage.emailsSent")}
                value={usage?.emailsSent || 0}
                icon={Mail}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Warnings */}
      {limits &&
        Object.entries(limits).some(
          ([_, data]) => !data.unlimited && data.percentUsed >= 80,
        ) && (
          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t(
                "usage.limitsWarning",
                "You're approaching some of your plan limits. Consider upgrading your plan or purchasing additional credits.",
              )}
            </AlertDescription>
          </Alert>
        )}

      {/* Credits Balance */}
      <Card>
        <CardHeader>
          <CardTitle>{t("usage.creditBalances")}</CardTitle>
          <CardDescription>{t("usage.creditBalancesDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CreditBalanceCard
              type="SMS"
              balance={subscription?.credits?.sms || 0}
              icon={MessageSquare}
              color="bg-green-100 dark:bg-green-950 text-green-600"
            />
            <CreditBalanceCard
              type="Envelope Overage"
              balance={subscription?.credits?.envelopeOverage || 0}
              icon={Mail}
              color="bg-blue-100 dark:bg-blue-950 text-blue-600"
            />
            <CreditBalanceCard
              type="API Overage"
              balance={subscription?.credits?.apiOverage || 0}
              icon={Server}
              color="bg-purple-100 dark:bg-purple-950 text-purple-600"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const UsageStatCard = ({
  title,
  value,
  limit,
  icon: Icon,
  color,
  showCredits,
}) => {
  const { t } = useTranslation();
  const safeValue = value ?? 0;
  const safeLimit = limit ?? 0;
  const isUnlimited = safeLimit === -1;
  const percentage =
    isUnlimited || !safeLimit ? 0 : Math.round((safeValue / safeLimit) * 100);
  const isWarning = percentage >= 80;
  const isDanger = percentage >= 95;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{safeValue.toLocaleString()}</div>
        {!showCredits && limit !== null && (
          <div className="text-xs text-muted-foreground mt-1">
            {isUnlimited ? (
              "Unlimited"
            ) : (
              <>
                of {safeLimit.toLocaleString()} ({percentage}%)
                {isDanger && (
                  <Badge variant="destructive" className="ml-2">
                    {t("common.critical")}
                  </Badge>
                )}
                {isWarning && !isDanger && (
                  <Badge variant="warning" className="ml-2">
                    {t("common.warning")}
                  </Badge>
                )}
              </>
            )}
          </div>
        )}
        {showCredits && (
          <div className="text-xs text-muted-foreground mt-1">Uses credits</div>
        )}
      </CardContent>
    </Card>
  );
};

const UsageBar = ({ name, used, limit, unlimited }) => {
  const safeUsed = used ?? 0;
  const safeLimit = limit ?? 0;
  const percentage =
    unlimited || safeLimit === -1
      ? 0
      : Math.round((safeUsed / safeLimit) * 100);
  const isWarning = percentage >= 80;
  const isDanger = percentage >= 95;

  const getUsageBarClass = (isNotUnlimited) => {
    if (isNotUnlimited) {
      if (isDanger) {
        return "[&>div]:bg-red-600";
      }
      return isWarning ? "[&>div]:bg-yellow-600" : "";
    }

    if (isDanger) {
      return "text-red-600";
    }
    return isWarning ? "text-yellow-600" : "text-muted-foreground";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{name}</span>
        <span className={getUsageBarClass()}>
          {safeUsed.toLocaleString()}
          {!unlimited && safeLimit !== -1 && (
            <span> / {safeLimit.toLocaleString()}</span>
          )}
          {(unlimited || safeLimit === -1) && (
            <span className="ml-1 text-xs">(unlimited)</span>
          )}
        </span>
      </div>
      {!unlimited && safeLimit !== -1 && (
        <Progress
          value={percentage}
          className={`h-2 ${getUsageBarClass(true)}`}
        />
      )}
    </div>
  );
};

const ActivityItem = ({ label, value, icon: Icon }) => {
  const safeValue = value ?? 0;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">{label}</span>
      </div>
      <span className="font-medium">{safeValue.toLocaleString()}</span>
    </div>
  );
};

const CreditBalanceCard = ({ type, balance, icon: Icon, color }) => {
  const safeBalance = balance ?? 0;
  return (
    <div className={`p-4 rounded-lg ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{type}</div>
          <div className="text-2xl font-bold">
            {safeBalance.toLocaleString()}
          </div>
        </div>
        <Icon className="h-8 w-8 opacity-50" />
      </div>
    </div>
  );
};

const formatLimitName = (key) => {
  const names = {
    envelopes: "Envelopes",
    templates: "Templates",
    teamMembers: "Team Members",
    signingLinks: "Signing Links",
    clauses: "Clauses",
    folders: "Folders",
    tags: "Tags",
    apiCalls: "API Calls",
  };
  return names[key] || key;
};

export default Usage;
